import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import { AutoAwesome as AssistantIcon } from '@mui/icons-material';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import VenueManager from './VenueManager';

/**
 * Kurzüberblick zum Venue-Bestellkanal in den Einstellungen; die eigentliche
 * Einrichtung läuft über den Assistenten.
 * Konzept: docs/general/venue_bestellknopf_taxibutler_konzept.md (§10.7)
 *
 * Bestellorte sind ausschliesslich Sache der Zentrale: Rules und Cloud Functions
 * lassen nur Admins schreiben, und diese Sektion erscheint fuer Unternehmer-Konten
 * deshalb gar nicht erst.
 */
const VenueChannelSettingsSection = ({ canManage }) => {
  const currentUser = useSelector(selectCurrentUser);
  const organizationId = currentUser?.organizationId || 'default_organization';
  // Nur ein ausdrueckliches `enabled: false` sperrt den Kanal; alles andere - auch
  // ein fehlendes Dokument - heisst offen. Der Schalter dazu ist absichtlich nicht
  // mehr in der Oberflaeche (siehe unten), deshalb hier nur die Warnung.
  const [disabled, setDisabled] = useState(false);
  const [venues, setVenues] = useState([]);
  const [venuesUnreadable, setVenuesUnreadable] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'appSettings', 'venueChannel'),
      (snapshot) => setDisabled(snapshot.exists() && snapshot.data().enabled === false),
      () => undefined
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      // Die Rules geben Venues nur im eigenen Mandanten frei. Eine Abfrage ohne
      // diesen Filter wird komplett abgelehnt — Firestore-Rules filtern nicht,
      // sie pruefen, ob die Abfrage ausschliesslich Erlaubtes treffen kann.
      query(collection(db, 'venues'), where('organizationId', '==', organizationId)),
      (snapshot) => {
        setVenuesUnreadable(false);
        setVenues(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      // Ein Fehler darf nicht als "keine Bestellorte" durchgehen: Genau diese
      // Verwechslung liess einen vorhandenen Bestellort als fehlend erscheinen.
      () => setVenuesUnreadable(true)
    );
    return unsubscribe;
  }, [organizationId]);

  const venueSummary = useMemo(() => {
    if (venuesUnreadable) return 'Bestellorte konnten nicht geladen werden';
    if (venues.length === 0) return 'Noch kein Bestellort eingerichtet';
    const base = `${venues.length} Bestellort${venues.length === 1 ? '' : 'e'} eingerichtet`;
    const pending = venues.filter((venue) => !venue.butlerLinkedAt).length;
    return pending > 0 ? `${base}, ${pending} davon im Taxi-Butler-Portal offen` : base;
  }, [venues, venuesUnreadable]);

  if (!canManage) return null;

  return (
    <Box>
      <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
            Bestellknopf — Taxi Butler
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Bestellgeräte von Taxi Butler in Hotels, Restaurants und Kliniken — das
          Personal drückt einen Knopf, die Fahrt landet direkt in der Disposition
        </Typography>
      </Box>

      {/* Es gibt keinen Schalter mehr — der Kanal ist offen, sobald ein Bestellort
          eingerichtet ist. Einzelne Geräte schaltet man im Taxi-Butler-Portal.
          Bleibt trotzdem der Fall, dass jemand in Firestore die Notbremse gezogen
          hat: Dann sieht der Support hier sofort, warum nichts ankommt. */}
      {disabled && (
        <Alert severity="error" sx={{ m: 2 }}>
          Der Bestellkanal ist abgeschaltet — es werden keine Bestellungen
          angenommen. Das lässt sich nur in Firestore ändern
          (<code>appSettings/venueChannel</code>, Feld <code>enabled</code>).
        </Alert>
      )}

      <Divider />

      {/* Die Verwaltung liegt bewusst auch hier und nicht nur im Assistenten: Ein
          Bestellort kommt spaeter dazu oder zieht um, ohne dass jemand deswegen
          die Einrichtung noch einmal durchlaufen will. */}
      <Box sx={{ px: 2, py: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Bestellorte
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {venueSummary}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {venuesUnreadable ? (
          <Alert severity="error">
            Bestellorte konnten nicht geladen werden.
          </Alert>
        ) : (
          <VenueManager venues={venues} organizationId={organizationId} onError={setError} />
        )}
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 2 }}>
        <Button
          component={RouterLink}
          to="/einstellungen/bestellknopf-einrichtung"
          variant="outlined"
          startIcon={<AssistantIcon />}
          disabled={!canManage}
        >
          Assistent
        </Button>
      </Box>
    </Box>
  );
};

export default VenueChannelSettingsSection;
